/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useMemo, useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';

import { phoenixShopHotelAmenities } from '../../HotelGalleryTitle/HQVQueries';
import { isServer } from '@marriott/mi-apollo-client-utils';
import { AmenitiesAPIData, AmenityProps, KeyAmenityProps, KeyAmenityFromAEMProps } from '../HotelInformation.types';
import { Styled } from './index.styles';
import { serverData } from '../HotelInformation';
import { useCheckBreakpoint } from '../../../hooks';
import { usePageContext } from '../../../context/PageContext';
import { hotelInformationMockData } from '../mock/hotelInformation';
import {
  complimentaryServicesCategoryCodes,
  beautyConfig,
  breakfastCodes,
  complimentaryServicesConfigList,
  selectServiceFeatureSortList,
  fullServiceFeatureSortList,
  brandConfig,
} from '../constants/ApplicationConstants';
import { logger } from '../../../utils';
import { useHQVContext } from '../../../context';

const { log } = logger({})('HotelGalleryInfoWrapper/Amenities');

export const Amenities: React.FC<AmenitiesAPIData> = () => {
  // log.debug('HotelAmenitiesComponent render');
  const { jsonContent, brandCode } = useContext(serverData);
  const { requestId, pageState, currentLocale, resolvedUrl, isAuthorMode } = usePageContext();
  const { hqvState } = useHQVContext();
  const propertyId = hqvState.propertyIDvalue;
  const hideBorder = resolvedUrl?.includes('/availabilityCalendar') ? true : false;
  const reqId = useMemo(() => {
    return requestId ?? `${Date.now()}`;
  }, [requestId]);

  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');
  const isViewportS = useCheckBreakpoint('viewportS');

  // Memoize all variables that affect the query,
  // to prevent re-triggering useQuery if component re-renders.
  const skipQuery = useMemo(() => {
    return !!pageState?.authorMode || !!isAuthorMode;
  }, [pageState, isAuthorMode]);

  const {
    // loading: amenitiesDataGraphLoading,
    error: amenitiesDataGraphError,
    data: amenitiesDataGraph,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery(phoenixShopHotelAmenities, {
    fetchPolicy: isServer ? 'network-only' : 'cache-first',
    variables: {
      propertyId: propertyId,
    },
    context: {
      headers: {
        // Any header, including x-request-id, can be passed in args with query.
        // If you don't pass it in the authLink will generate a random ID.
        'x-request-id': reqId,
        'accept-language': currentLocale,
      },
    },
    skip: skipQuery,
    // onCompleted: data => {
    //   log.debug('phoenixShopHotelAmenities complete: ', data);
    // },
  });

  log.debug('amenitiesDataGraph: ', amenitiesDataGraph);
  if (amenitiesDataGraphError) {
    log.debug('phoenixShopHotelAmenities failed: ', amenitiesDataGraphError);
  }

  const amenitiesData = useMemo(() => {
    return pageState?.authorMode || isAuthorMode ? hotelInformationMockData.amenitiesData : amenitiesDataGraph;
  }, [pageState?.authorMode, amenitiesDataGraph, isAuthorMode]);
  const tempAmenities = useMemo(() => {
    return amenitiesData?.property?.facilitiesAndServices;
  }, [amenitiesData]);

  const healthAndServices: string[] = useMemo(() => {
    const tempHealthAndServices: string[] = [];
    tempAmenities?.forEach((amenity: AmenityProps) => {
      if (
        amenity?.type?.code === 'Guest Facilities' ||
        amenity?.type?.code === 'Guest Services' ||
        amenity?.type?.code === 'Shopping & Stores'
      ) {
        if (beautyConfig.includes(amenity?.categoryType?.code) && amenity?.details) {
          tempHealthAndServices.push(amenity.details[0].key);
        }
      }
    });
    return tempHealthAndServices;
  }, [tempAmenities]);

  const servicesList: string[] = useMemo(() => {
    const tempSercvicesList: string[] = [];
    tempAmenities?.forEach((amenity: AmenityProps) => {
      if (
        amenity?.type?.code === 'Guest Facilities' ||
        amenity?.type?.code === 'Guest Services' ||
        amenity?.type?.code === 'Shopping & Stores'
      ) {
        if (beautyConfig.includes(amenity?.categoryType?.code) && amenity?.details) {
          healthAndServices.push(amenity.details[0].key);
        } else if (breakfastCodes.includes(amenity?.categoryType?.code)) {
          const value = `${amenity?.description}: ${amenity.details[0]?.key}`;
          tempSercvicesList.push(value);
        } else {
          tempSercvicesList.push(amenity?.description);
        }
      } else if (
        amenity?.type?.code &&
        complimentaryServicesConfigList.includes(amenity.type.code) &&
        complimentaryServicesCategoryCodes.includes(amenity?.categoryType?.code)
      ) {
        tempSercvicesList.push(amenity.description);
      }
    });

    return tempSercvicesList;
  }, [healthAndServices, tempAmenities]);

  let lookup: KeyAmenityFromAEMProps[];
  if (brandConfig.includes(brandCode)) {
    lookup = selectServiceFeatureSortList;
  } else {
    lookup = fullServiceFeatureSortList;
  }

  const allAmenities: string[] = useMemo(() => {
    const amenListTemp: string[] = [];
    let idx = 0;
    while (idx < servicesList.length) {
      if (!amenListTemp.includes(servicesList[idx])) {
        amenListTemp.push(servicesList[idx]);
      }
      idx += 1;
    }
    return amenListTemp;
  }, [servicesList]);

  const validKeyAmenitiesList: string[] = useMemo(() => {
    const keyAmenList: string[] = [];
    lookup.forEach((amenity: KeyAmenityFromAEMProps) => {
      keyAmenList.push(amenity['amenitiesTagId']);
    });
    return keyAmenList;
  }, [lookup]);

  const tempKeyAmenities: KeyAmenityProps[] = amenitiesData?.property.matchingSearchFacets;

  // generate memoized keyAmentiesList based on tempKeyAmenities
  const keyAmenitiesList: KeyAmenityProps[] = useMemo(() => {
    const keyAmenTempList: KeyAmenityProps[] = [];
    tempKeyAmenities?.forEach((keyAmenity: KeyAmenityProps) => {
      if (validKeyAmenitiesList.includes(keyAmenity?.dimension?.code)) {
        keyAmenTempList.push(keyAmenity);
      }
    });
    return keyAmenTempList;
  }, [tempKeyAmenities, validKeyAmenitiesList]);

  const [seeMore, setSeeMore] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const seeMoreButton = useRef(null);
  const seeFewerButton = useRef(null);
  // const { hqvDispatch } = useHQVContext();
  const clickTrackingLoc = 'HQV AEM Page';
  //The values 9 and 6 are based on the Visual Designs provided.
  const desktopCount = 9;
  const mobileCount = 6;
  let amenitiesCountLimit = desktopCount;
  if (isViewportS && !isViewportM) {
    amenitiesCountLimit = mobileCount;
  }

  function formatIconName(icon: string): string {
    if (!icon) {
      return 'icon-check';
    }
    const formattedIcon = 'icon-' + icon;
    return 'icon-check ' + formattedIcon;
  }

  const amenitiesList: { icon: string; id: string; name: string }[] = useMemo(() => {
    const createdAmenitiesList = [];
    if (keyAmenitiesList) {
      for (let i = 0; i < keyAmenitiesList?.length; i++) {
        let icon = '';
        if (keyAmenitiesList[i]?.dimension?.code) {
          icon = formatIconName(keyAmenitiesList[i]?.dimension?.code);
        }
        const id = keyAmenitiesList[i]?.dimension?.code;
        const name = keyAmenitiesList[i]?.dimension?.description;
        createdAmenitiesList.push({ icon: icon, id: id, name: name });
      }
    }

    if (allAmenities) {
      for (let i = 0; i < allAmenities?.length; i++) {
        const icon = formatIconName('');
        const id = '';
        const name = allAmenities[i];
        createdAmenitiesList.push({ icon: icon, id: id, name: name });
      }
    }

    return createdAmenitiesList;
  }, [keyAmenitiesList, allAmenities]);

  const showMoreFlag = useMemo(() => {
    return amenitiesList.length > amenitiesCountLimit;
  }, [amenitiesList, amenitiesCountLimit]);

  useEffect(() => {
    if (hasBeenClicked) {
      if (seeMore) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((seeFewerButton as any).current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (seeFewerButton as any).current.focus();
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((seeMoreButton as any).current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (seeMoreButton as any).current.focus();
        }
      }
    }
  }, [hasBeenClicked, seeMore]);

  // eslint-disable-next-line no-extra-boolean-cast
  return amenitiesList && amenitiesList.length > 0 ? (
    <Styled>
      <div
        id="hqv-amenities-section"
        className={clsx(
          'amenities-component',
          'space-buffer',
          'adjustable-margin-element',
          `${hqvState.modalOpenState ? 'hqv-modal' : ''}`
        )}
      >
        <h2 className={clsx(isViewportL ? 't-title-s' : 't-subtitle-xl', 'component-heading')}>
          {jsonContent?.amenitiesLabel}
        </h2>
        <div className={`amenities-content ${hideBorder ? 'remove-border' : ''}`}>
          <div className="component-content">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {amenitiesList?.slice(0, amenitiesCountLimit).map((amenity: any, index: number) => (
              <div className={clsx('col', 'm-icon-text', 'amenity-list-item')} key={index}>
                <span className={clsx('icon-m', amenity.icon, 'icon-decorative', 'icon-holder')}></span>
                <span className={clsx('t-font-m', 'amenity-name')}>{amenity.name}</span>
              </div>
            ))}
          </div>
          <div className={clsx(`component-content`, seeMore ? '' : 'hide-content')}>
            {amenitiesList?.slice(amenitiesCountLimit).map((amenity: any, index: number) => (
              <div className={clsx('col', 'm-icon-text', 'amenity-list-item')} key={index}>
                <div className={clsx('icon-m', amenity.icon, 'icon-decorative', 'icon-holder')} />
                <div className={clsx('t-font-m', 'amenity-name')}>{amenity.name}</div>
              </div>
            ))}
          </div>
          {!seeMore && showMoreFlag && (
            <div
              ref={seeMoreButton}
              tabIndex={0}
              role="button"
              className={clsx(
                'see-amenities-button',
                'custom_click_track',
                !seeMore && showMoreFlag ? '' : 'hide-content'
              )}
              onClick={(): void => {
                setHasBeenClicked(true);
                setSeeMore(true);
              }}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                  setHasBeenClicked(true);
                  setSeeMore(true);
                }
              }}
              {...{ custom_click_track_value: `${clickTrackingLoc}| See all amenities clicked |internal` }}
            >
              <div className={clsx('t-label-alt-xs', 'label-text')}>{jsonContent?.seeAllPropertyAmenitiesLabel}</div>
              <div className={clsx('icon-dropdown-down', 'label-icon')} />
            </div>
          )}
          {seeMore && showMoreFlag && (
            <div
              ref={seeFewerButton}
              tabIndex={0}
              className={clsx(
                'see-amenities-button',
                'custom_click_track',
                seeMore && showMoreFlag ? '' : 'hide-content'
              )}
              onClick={(): void => {
                setHasBeenClicked(true);
                setSeeMore(false);
              }}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                  setHasBeenClicked(true);
                  setSeeMore(false);
                }
              }}
              {...{ custom_click_track_value: `${clickTrackingLoc}| See fewer amenities clicked |internal` }}
            >
              <div className={clsx('t-label-alt-xs', 'label-text')}>{jsonContent?.seeFewerAmenitiesLabel}</div>
              <div className={clsx('icon-dropdown-up', 'label-icon')} />
            </div>
          )}
        </div>
      </div>
    </Styled>
  ) : null;
};
